import { useState, useEffect, useMemo } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { ScrollArea } from '../ui/scroll-area';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import {
  CreditCard, Check, X, Calendar, Clock, Sparkles, Shield, Lock,
  ChevronRight, AlertCircle, CheckCircle, Users
} from 'lucide-react';
import axios from 'axios';
import { formatStripeError } from '../../lib/stripeErrors';
import { trackPurchase } from '../../lib/analytics';
import { InAppBrowserWarning } from '../InAppBrowserWarning';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const cardElementOptions = {
  style: {
    base: {
      fontSize: '16px',
      color: '#ffffff',
      '::placeholder': { color: '#6b7280' },
      iconColor: '#a855f7',
    },
    invalid: { color: '#ef4444', iconColor: '#ef4444' },
  },
};

const CardForm = ({
  onSuccess,
  processing,
  setProcessing,
  paymentType,
  bookingIds,
  isMulti,
  totalAmount,
  installmentPreview,
  clientSecret: existingSecret,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [clientSecret, setClientSecret] = useState(existingSecret || null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (existingSecret) setClientSecret(existingSecret);
  }, [existingSecret]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements || !clientSecret) return;
    setProcessing(true);
    setError(null);

    try {
      const { error: stripeError, setupIntent } = await stripe.confirmCardSetup(clientSecret, {
        payment_method: { card: elements.getElement(CardElement) },
      });
      if (stripeError) {
        setError(formatStripeError(stripeError));
        setProcessing(false);
        return;
      }

      if (setupIntent.status === 'succeeded') {
        let confirmRes;
        if (isMulti) {
          // Multi-booking sibling group endpoints
          if (paymentType === 'installments') {
            confirmRes = await axios.post(`${API_URL}/api/installments/confirm-multi`, {
              bootcamp_booking_ids: bookingIds,
              payment_method_id: setupIntent.payment_method,
            });
          } else {
            confirmRes = await axios.post(`${API_URL}/api/installments/pay-full-multi`, {
              bootcamp_booking_ids: bookingIds,
              payment_method_id: setupIntent.payment_method,
            });
          }
        } else {
          // Single-booking legacy path
          if (paymentType === 'installments') {
            confirmRes = await axios.post(`${API_URL}/api/installments/confirm`, {
              bootcamp_booking_id: bookingIds[0],
              payment_method_id: setupIntent.payment_method,
            });
          } else {
            confirmRes = await axios.post(`${API_URL}/api/installments/pay-full`, null, {
              params: {
                bootcamp_booking_id: bookingIds[0],
                payment_method_id: setupIntent.payment_method,
              },
            });
          }
        }
        onSuccess(confirmRes.data);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Payment failed');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <InAppBrowserWarning />
      <div className="p-4 bg-neutral-800 rounded-lg border border-neutral-700">
        <CardElement options={cardElementOptions} />
      </div>

      {error && (
        <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm flex items-center gap-2">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}

      <div className="flex items-center gap-2 text-white/40 text-xs">
        <Lock className="w-3 h-3" />
        <span>Secure payment powered by Stripe</span>
      </div>

      <Button
        type="submit"
        disabled={!stripe || !clientSecret || processing}
        className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 h-12"
        data-testid="confirm-payment-btn"
      >
        {processing ? (
          <>
            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
            Processing...
          </>
        ) : (
          <>
            <CreditCard className="w-5 h-5 mr-2" />
            {paymentType === 'installments'
              ? `Pay First Installment (£${installmentPreview?.[0]?.amount?.toFixed(2) || (totalAmount / 3).toFixed(2)})`
              : `Pay £${totalAmount?.toFixed(2)} Now`}
          </>
        )}
      </Button>
    </form>
  );
};

const BootcampPaymentModal = ({ booking, bookings, bootcamp, open, onClose, onSuccess }) => {
  // Normalise into array (`bookings` preferred; fallback to single `booking`)
  const bookingArray = useMemo(() => {
    if (Array.isArray(bookings) && bookings.length > 0) return bookings;
    if (booking) return [booking];
    return [];
  }, [bookings, booking]);

  const isMulti = bookingArray.length > 1;
  const bookingIds = useMemo(() => bookingArray.map((b) => b.id), [bookingArray]);

  const [step, setStep] = useState('select');
  const [paymentType, setPaymentType] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [paymentResult, setPaymentResult] = useState(null);
  const [installmentPreview, setInstallmentPreview] = useState(null);
  const [setupClientSecret, setSetupClientSecret] = useState(null);

  useEffect(() => {
    if (open && bookingArray.length > 0) fetchInstallmentPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, bookingIds.join(',')]);

  const fetchInstallmentPreview = async () => {
    try {
      let res;
      if (isMulti) {
        res = await axios.post(
          `${API_URL}/api/installments/create-setup-intent-multi?bootcamp_booking_ids=${bookingIds.join(',')}`,
        );
      } else {
        res = await axios.post(
          `${API_URL}/api/installments/create-setup-intent?bootcamp_booking_id=${bookingIds[0]}`,
        );
      }
      setInstallmentPreview(res.data.installment_preview);
      setSetupClientSecret(res.data.client_secret);
    } catch (err) {
      console.error('Failed to fetch installment preview:', err?.response?.data || err);
    }
  };

  const handlePaymentSuccess = (result) => {
    // GTM purchase event with the actual amount charged now
    const chargedNow = paymentType === 'installments'
      ? (result?.installments?.[0]?.amount ?? installmentPreview?.[0]?.amount)
      : totalAmount;
    trackPurchase(chargedNow, `bootcamp_${bookingIds.join('_')}`);

    setPaymentResult(result);
    setStep('success');
    toast.success(result.message || 'Payment successful!');
  };

  const totalAmount = useMemo(
    () => bookingArray.reduce((acc, b) => acc + Number(b.amount || 0), 0) || bootcamp?.price || 0,
    [bookingArray, bootcamp],
  );

  if (bookingArray.length === 0 || !bootcamp) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md p-0 bg-neutral-900 border-neutral-800 overflow-hidden">
        {/* Header */}
        <div className="p-6 bg-gradient-to-r from-purple-500/20 to-pink-500/20 border-b border-neutral-800">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-8 h-8 rounded-full bg-black/30 flex items-center justify-center text-white hover:bg-black/50 z-10"
            data-testid="close-payment-modal"
          >
            <X className="w-4 h-4" />
          </button>
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-white">Complete Payment</DialogTitle>
          </DialogHeader>
          <p className="text-white/60 text-sm mt-1">{bootcamp.name}</p>
        </div>

        <ScrollArea className="max-h-[70vh]">
          {/* Step: Select Payment Type */}
          {step === 'select' && (
            <div className="p-6 space-y-4">
              {/* Booking Summary */}
              <div className="bg-neutral-800/50 rounded-lg p-4 space-y-2">
                {isMulti ? (
                  <>
                    <div className="flex items-center gap-2 text-white/60 text-sm">
                      <Users className="w-4 h-4" />
                      <span>{bookingArray.length} children booked</span>
                    </div>
                    <div className="space-y-1 pt-1">
                      {bookingArray.map((b) => (
                        <div key={b.id} className="flex justify-between text-sm" data-testid={`group-line-${b.id}`}>
                          <span className="text-white/80">
                            {b.student_name}
                            {b?.sibling_discount_info && (
                              <span className="text-pink-400 ml-2 text-xs">30% off</span>
                            )}
                          </span>
                          <span className="text-white">£{Number(b.amount || 0).toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex justify-between text-sm">
                      <span className="text-white/60">Student</span>
                      <span className="text-white">{bookingArray[0].student_name}</span>
                    </div>
                  </>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-white/60">Camp</span>
                  <span className="text-white">{bootcamp.name}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-white/60">Dates</span>
                  <span className="text-white">
                    {new Date(bootcamp.start_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} –{' '}
                    {new Date(bootcamp.end_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                  </span>
                </div>
                <div className="border-t border-neutral-700 pt-2 mt-2">
                  <div className="flex justify-between">
                    <span className="text-white font-medium">Total</span>
                    <span className="text-xl font-bold text-purple-400" data-testid="payment-total">
                      £{totalAmount.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>

              <h3 className="text-white font-semibold">Choose Payment Method</h3>

              {/* Pay in Full */}
              <button
                onClick={() => { setPaymentType('full'); setStep('payment'); }}
                className="w-full p-4 rounded-lg border-2 border-neutral-700 hover:border-neutral-600 text-left transition-all"
                data-testid="pay-full-option"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-white font-medium">Pay in Full</h4>
                    <p className="text-white/60 text-sm">
                      One-time payment of £{totalAmount.toFixed(2)}
                      {isMulti && ' for the whole group'}
                    </p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-white/40" />
                </div>
              </button>

              {/* Pay in 3 */}
              <button
                onClick={() => { setPaymentType('installments'); setStep('payment'); }}
                className="w-full p-4 rounded-lg border-2 border-neutral-700 hover:border-neutral-600 text-left transition-all"
                data-testid="pay-installments-option"
              >
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <h4 className="text-white font-medium">Pay in 3 Installments</h4>
                      <Badge className="bg-green-500/20 text-green-400 border-green-500/30 text-xs">Popular</Badge>
                    </div>
                    <p className="text-white/60 text-sm">
                      Split {isMulti ? "the group's total" : ''} into 3 easy payments
                    </p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-white/40" />
                </div>

                {installmentPreview && (
                  <div className="space-y-2 pt-3 border-t border-neutral-700">
                    {installmentPreview.map((inst, i) => (
                      <div key={i} className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-3 h-3 text-purple-400" />
                          <span className="text-white/70">
                            {inst.is_immediate
                              ? 'Today'
                              : new Date(inst.due_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                          </span>
                        </div>
                        <span className="text-white font-medium">£{inst.amount.toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </button>
            </div>
          )}

          {/* Step: Payment Form */}
          {step === 'payment' && (
            <div className="p-6 space-y-4">
              <button
                onClick={() => setStep('select')}
                className="text-purple-400 text-sm hover:underline flex items-center gap-1"
              >
                <ChevronRight className="w-4 h-4 rotate-180" />
                Back to payment options
              </button>

              <div className="bg-neutral-800/50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  {paymentType === 'installments' ? (
                    <>
                      <Clock className="w-4 h-4 text-purple-400" />
                      <span className="text-white font-medium">Pay in 3 Installments</span>
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-4 h-4 text-purple-400" />
                      <span className="text-white font-medium">Pay in Full</span>
                    </>
                  )}
                </div>
                {paymentType === 'installments' && installmentPreview && (
                  <div className="text-sm text-white/60 space-y-1">
                    {installmentPreview.map((inst, i) => (
                      <div key={i} className="flex justify-between">
                        <span>{inst.is_immediate ? 'Due now' : inst.due_date}</span>
                        <span className="text-white">£{inst.amount.toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                )}
                {paymentType === 'full' && (
                  <p className="text-sm text-white/60">Total: £{totalAmount.toFixed(2)}</p>
                )}
              </div>

              <Elements stripe={stripePromise}>
                <CardForm
                  onSuccess={handlePaymentSuccess}
                  processing={processing}
                  setProcessing={setProcessing}
                  paymentType={paymentType}
                  bookingIds={bookingIds}
                  isMulti={isMulti}
                  totalAmount={totalAmount}
                  installmentPreview={installmentPreview}
                  clientSecret={setupClientSecret}
                />
              </Elements>

              <div className="flex items-center justify-center gap-4 pt-2">
                <div className="flex items-center gap-1 text-white/30 text-xs">
                  <Shield className="w-3 h-3" />
                  <span>256-bit SSL</span>
                </div>
                <div className="flex items-center gap-1 text-white/30 text-xs">
                  <Lock className="w-3 h-3" />
                  <span>PCI Compliant</span>
                </div>
              </div>
            </div>
          )}

          {/* Step: Success */}
          {step === 'success' && (
            <div className="p-6 text-center space-y-4">
              <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle className="w-8 h-8 text-green-400" />
              </div>
              <h3 className="text-white text-xl font-bold">Payment Successful!</h3>
              <p className="text-white/60">
                {paymentType === 'installments'
                  ? 'Your first installment has been processed. Remaining payments will be charged automatically.'
                  : isMulti
                    ? `Payment for all ${bookingArray.length} children has been processed.`
                    : 'Your payment has been processed successfully.'}
              </p>

              {paymentResult?.card && (
                <div className="bg-neutral-800 rounded-lg p-3 inline-flex items-center gap-2">
                  <CreditCard className="w-4 h-4 text-white/40" />
                  <span className="text-white text-sm capitalize">{paymentResult.card.brand}</span>
                  <span className="text-white/60 text-sm">•••• {paymentResult.card.last4}</span>
                </div>
              )}

              {paymentType === 'installments' && paymentResult?.installments && (
                <div className="bg-neutral-800/50 rounded-lg p-4 text-left mt-4">
                  <h4 className="text-white font-medium text-sm mb-3">Payment Schedule</h4>
                  <div className="space-y-2">
                    {paymentResult.installments.map((inst, i) => (
                      <div key={i} className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2">
                          {inst.status === 'paid' ? (
                            <Check className="w-4 h-4 text-green-400" />
                          ) : (
                            <Clock className="w-4 h-4 text-white/40" />
                          )}
                          <span className={inst.status === 'paid' ? 'text-white' : 'text-white/60'}>
                            {inst.due_date}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-white">£{inst.amount.toFixed(2)}</span>
                          {inst.status === 'paid' && (
                            <Badge className="bg-green-500/20 text-green-400 text-xs">Paid</Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <Button
                onClick={() => { onClose(); if (onSuccess) onSuccess(); }}
                className="bg-gradient-to-r from-purple-500 to-pink-500 mt-4"
                data-testid="payment-done-btn"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Done
              </Button>
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

export default BootcampPaymentModal;
